// /frontend/src/SideBarComponents/CostAnalysis/CostAnalysisContext.tsx
// FINAL, SIMPLIFIED VERSION - NO UPDATE FUNCTION

import React, { createContext, useState, useContext, useCallback, ReactNode, useRef, useEffect, useMemo } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { FilterOptions, CostAnalysisGroupBy, CostAnalysisInput, CostAnalysisPayload, DataSourceOption } from './CostAnalysis';
import dayjs from 'dayjs';

// --- GraphQL Queries ---
const GET_FILTER_OPTIONS = gql`
  query GetFilterOptions($input: FilterOptionsInput!) { getFilterOptions(input: $input) { services, regions, projectIds, skus, labelKeys, dataSources { id, name, providerType, isSyncing } } }
`;
const GET_COST_ANALYSIS = gql`
  query GetCostAnalysis($input: CostAnalysisInput!) { getCostAnalysis(input: $input) { kpis { totalCost, totalCostChangePercentage, totalForecastedCost, totalForecastedCostChangePercentage, topGroup, topGroupCost, topGroupCredits, totalCredits, topGroupCostChangePercentage }, timeSeries { date, groups { name, cost } }, breakdown { group, cost, credits, discount, usage, totalCost } } }
`;

// --- Type Definitions ---
interface GetFilterOptionsData { getFilterOptions: FilterOptions; }
interface GetFilterOptionsVars { input: { tenantId: string; dateRange: { start: string; end: string; } } }
interface GetCostAnalysisVars { input: CostAnalysisInput; }

// --- Context Definition (NO updatePreloadedState) ---
interface ICostAnalysisContext {
  isLoading: boolean;
  isLoaded: boolean;
  error: any | null;
  filterOptions: FilterOptions | null;
  initialCostData: CostAnalysisPayload | null;
  preloadedQueryVariables: CostAnalysisInput | null;
  preloadCostAnalysisData: (tenantId: string) => void;
  clearPreloadedData: () => void;
}

const CostAnalysisContext = createContext<ICostAnalysisContext>({
  isLoading: false, isLoaded: false, error: null, filterOptions: null, initialCostData: null,
  preloadedQueryVariables: null,
  preloadCostAnalysisData: () => console.warn('Provider not initialized'),
  clearPreloadedData: () => console.warn('Provider not initialized'),
});

export const useCostAnalysisContext = () => useContext(CostAnalysisContext);

// --- Provider Component ---
export const CostAnalysisProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<any | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [initialCostData, setInitialCostData] = useState<CostAnalysisPayload | null>(null);
  const [preloadedQueryVariables, setPreloadedQueryVariables] = useState<CostAnalysisInput | null>(null);

  const [fetchFilterOptions] = useLazyQuery<GetFilterOptionsData, GetFilterOptionsVars>(GET_FILTER_OPTIONS, { fetchPolicy: 'network-only' });
  const [fetchInitialData] = useLazyQuery<CostAnalysisPayload, GetCostAnalysisVars>(GET_COST_ANALYSIS, { fetchPolicy: 'network-only' });

  const stateRef = useRef({ isLoading, isLoaded });
  useEffect(() => { stateRef.current = { isLoading, isLoaded }; }, [isLoading, isLoaded]);

  const preloadCostAnalysisData = useCallback(async (tenantId: string) => {
    if (stateRef.current.isLoading || stateRef.current.isLoaded || !tenantId) return;

    console.log(`[CostAnalysisContext] Preloading data for tenant: ${tenantId}`);
    setIsLoading(true);
    setError(null);

    try {
        const { data: filterOptionsData, error: filterOptionsError } = await fetchFilterOptions({
            variables: { input: { tenantId, dateRange: { start: dayjs().subtract(29, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') } } },
        });
        if (filterOptionsError) throw filterOptionsError;
        const fetchedFilterOptions = filterOptionsData?.getFilterOptions;
        if (!fetchedFilterOptions) throw new Error("Received no data for filter options.");
        setFilterOptions(fetchedFilterOptions);

        const LOCAL_STORAGE_KEY = `costAnalysisFilters_${tenantId}`;
        const savedFiltersJSON = localStorage.getItem(LOCAL_STORAGE_KEY);
        let queryVariables: CostAnalysisInput;
        try {
            if (!savedFiltersJSON) throw new Error("No saved filters.");
            const savedState = JSON.parse(savedFiltersJSON);
            const validDataSourceMap = new Map(fetchedFilterOptions.dataSources.map(ds => [ds.id, ds]));
            const reconciledDataSources = (savedState.selectedDataSources || []).filter((id: string) => {
                const dataSource = validDataSourceMap.get(id);
                return dataSource && !dataSource.isSyncing;
            });
            const groupByMapping: { [key: string]: CostAnalysisGroupBy } = { 'Services': CostAnalysisGroupBy.service, 'Data Sources': CostAnalysisGroupBy.dataSourceId, 'Region': CostAnalysisGroupBy.region, 'Project': CostAnalysisGroupBy.projectId, 'SKU': CostAnalysisGroupBy.sku };
            const labelFilters = (savedState.labelFilters || []).flatMap((pair: any) => pair.values.map((val: string) => ({ key: pair.key, value: val })));
            queryVariables = {
                tenantId: tenantId,
                groupBy: groupByMapping[savedState.groupBy] || CostAnalysisGroupBy.service,
                dateRange: {
                    start: savedState.customStartDate ? dayjs(savedState.customStartDate).format('YYYY-MM-DD') : dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
                    end: savedState.customEndDate ? dayjs(savedState.customEndDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                },
                filters: {
                    dataSourceIds: reconciledDataSources,
                    services: savedState.selectedServices || [],
                    regions: savedState.dynamicFilterState?.['Region']?.selected || [],
                    skus: savedState.dynamicFilterState?.['SKU']?.selected || [],
                    projectIds: savedState.dynamicFilterState?.['Project']?.selected || [],
                    labels: labelFilters.length > 0 ? labelFilters : undefined,
                }
            };
            console.log("[CostAnalysisContext] Successfully built query variables from localStorage.");
        } catch (e) {
            console.log("[CostAnalysisContext] No/invalid saved state found, using default filters for preload.");
            const defaultSelectableSources = fetchedFilterOptions.dataSources.filter(ds => !ds.isSyncing).map(ds => ds.id);
            queryVariables = {
                tenantId: tenantId,
                groupBy: CostAnalysisGroupBy.service,
                dateRange: { start: dayjs().subtract(29, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') },
                filters: { dataSourceIds: defaultSelectableSources },
            };
        }
        setPreloadedQueryVariables(queryVariables);
        const { data: costData, error: costError } = await fetchInitialData({ variables: { input: queryVariables } });
        if (costError) throw costError;
        setInitialCostData(costData ?? null);
        if (costData) { setIsLoaded(true); }
    } catch (e) {
      console.error("Failed to preload Cost Analysis data", e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [fetchFilterOptions, fetchInitialData]);

  const clearPreloadedData = useCallback(() => {
    console.log('[CostAnalysisContext] Clearing preloaded data.');
    setIsLoaded(false); setIsLoading(false); setFilterOptions(null);
    setInitialCostData(null); setPreloadedQueryVariables(null); setError(null);
  }, []);

  const value = useMemo(() => ({
    isLoading, isLoaded, error, filterOptions, initialCostData,
    preloadedQueryVariables, preloadCostAnalysisData, clearPreloadedData,
  }), [
    isLoading, isLoaded, error, filterOptions, initialCostData,
    preloadedQueryVariables, preloadCostAnalysisData, clearPreloadedData
  ]);

  return ( <CostAnalysisContext.Provider value={value}>{children}</CostAnalysisContext.Provider> );
};